/**
 * Integration error rendering — frontend half of the integration-error pattern.
 *
 * Backend contract (see
 * approveit-backend/integration/softledger/error_mapping.py):
 *
 *   error_code: "INTG.BL.024"
 *   details: {
 *     integration: "softledger" | "xero" | ...,
 *     reason_code: "<known_slug>" | "unknown",
 *     raw_error:   "<original API error>" | null,
 *     ...other diagnostic fields...
 *   }
 *
 * We resolve a localized message in this order:
 *   1. integration:<integration>.errors.<reason_code> (the happy path)
 *   2. integration:<integration>.errors.unknown with raw_error interpolated
 *   3. integration:errors.fallback / errors.missingReason
 */

import i18n from '@src/i18n'

const I18N_NS = 'integration'

const isString = (v: unknown): v is string => typeof v === 'string' && v.length > 0

const lookup = (key: string, options?: Record<string, unknown>): string | null => {
  const result = i18n.t(key, { ns: I18N_NS, ...options }) as string
  // i18next returns the key itself when no translation is registered.
  if (!result || result === key || result === `${I18N_NS}:${key}`) return null
  return result
}

export const resolveIntegrationErrorMessage = (
  details: Record<string, unknown>,
): string => {
  const integration = isString(details.integration) ? details.integration : null
  const reasonCode = isString(details.reason_code) ? details.reason_code : null
  const rawError = isString(details.raw_error) ? details.raw_error : null

  // 1. Best case: known integration + known reason → fully localized message.
  if (integration && reasonCode && reasonCode !== 'unknown') {
    const localized = lookup(`${integration}.errors.${reasonCode}`)
    if (localized) return localized
  }

  // 2. Known integration with raw error text → use the integration's "unknown"
  //    template (which interpolates the error) or the cross-integration fallback.
  if (integration && rawError) {
    const unknownLocalized = lookup(`${integration}.errors.unknown`, {
      error: rawError,
    })
    if (unknownLocalized) return unknownLocalized
    return i18n.t('errors.fallback', {
      ns: I18N_NS,
      integration,
      error: rawError,
    }) as string
  }

  // 3. Known integration but no raw error → tell the user we couldn't sync,
  //    without inventing a non-reason. Skip the integration's "unknown" key
  //    here because it would interpolate an empty string and read awkwardly.
  if (integration) {
    return i18n.t('errors.missingReason', {
      ns: I18N_NS,
      integration,
    }) as string
  }

  // 4. Last-ditch: no integration name available.
  return rawError ?? (i18n.t('common:toast.somethingWentWrong') as string)
}
